import React, { useState, useMemo, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
  LabelList,
} from "recharts";
import { Typography, Box, IconButton, Divider } from "@mui/material";
import { WidgetContainer } from "../widgetContainer/widgetContainer";
import { DownloadExcel } from "../../../functions/downloadExcel";

const DEFAULT_COLORS = ["#7E4553", "#70C930", "#F9CA00", "#BA1650", "#E97F03", "#9D419B"];

const StackedBarChartGeneric = ({
  data,
  title,
  formatAsCurrency = false,
  description,
  colorOverrides = {},
  isCurrency = false,
  LegendOnLeft = false,
  insights = false,
  onInsights = () => {},
}) => {
  const [activeVendor, setActiveVendor] = useState("ALL");

  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const vendors = useMemo(() => {
    const extractedVendors = new Set();
    safeData.forEach((entry) => {
      Object.keys(entry).forEach((key) => {
        if (key !== "month") extractedVendors.add(key);
      });
    });
    return ["ALL", ...Array.from(extractedVendors)];
  }, [safeData]);

  // FIX: Prevent infinite loop by ensuring activeVendor is always valid
  useEffect(() => {
    if (!vendors.includes(activeVendor)) {
      setActiveVendor("ALL");
    }
  }, [vendors, activeVendor]);

  const transformedData = useMemo(() => {
    if (!safeData.length) return [];

    const result = safeData.map((entry) => {
      const transformedEntry = { month: entry.month };
      let totalValue = 0;

      if (activeVendor === "ALL") {
        const lobTotals = {};
        Object.entries(entry).forEach(([vendor, lobData]) => {
          if (vendor !== "month" && typeof lobData === "object") {
            Object.entries(lobData).forEach(([lob, value]) => {
              lobTotals[lob] = (lobTotals[lob] || 0) + (value || 0);
              totalValue += value || 0;
            });
          } else if (vendor !== "month") {
            lobTotals[vendor] = (lobTotals[vendor] || 0) + lobData;
            totalValue += lobData;
          }
        });
        Object.entries(lobTotals).forEach(([lob, total]) => {
          transformedEntry[lob] = total;
        });
      } else {
        const lobData = entry[activeVendor];

        if (lobData !== undefined && lobData !== null && typeof lobData === "object") {
          Object.entries(lobData).forEach(([lob, value]) => {
            transformedEntry[lob] = value || 0;
            totalValue += value || 0;
          });
        } else if (lobData !== undefined) {
          transformedEntry[activeVendor] = lobData;
          totalValue = lobData;
        }
      }

      transformedEntry._totalValue = totalValue;
      return transformedEntry;
    });

    return result;
  }, [safeData, activeVendor]);

  const lobKeys = useMemo(() => {
    if (transformedData.length === 0) return [];
    return Object.keys(transformedData[0]).filter(
      (key) => key !== "month" && key !== "_totalValue"
    );
  }, [transformedData]);

  const getColor = (key, index) =>
    colorOverrides[key] || DEFAULT_COLORS[index % DEFAULT_COLORS.length];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <Box
        sx={{
          backgroundColor: "#2b2b2b",
          border: "1px solid #444",
          borderRadius: "6px",
          padding: "10px 12px",
          color: "#fff",
          fontSize: 12,
          minWidth: "160px",
          opacity: 0.9,
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: 13,
            textTransform: "uppercase",
          }}
        >
          {label}
        </Typography>

        <Divider sx={{ my: 1, borderColor: "#555" }} />

        {payload.map((entry, index) => (
          <Box
            key={`item-${index}`}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ fontSize: 12, mt: 0.5 }}
          >
            <Box display="flex" alignItems="center" sx={{ mr: 5 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: entry.color,
                  marginRight: 1,
                }}
              />
              <span>{entry.name}</span>
            </Box>
            <span>
              {typeof entry.value === "number" && isCurrency
                ? `$${entry.value.toLocaleString()}`
                : entry.value}
            </span>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <WidgetContainer
      title={title}
      description={description}
      updatedAt={data && data[0]?.updatedAt}
      loaded={data}
      download={true}
      maximize={true}
      insights={insights}
      onInsights={onInsights}
      onDownload={() => {
        DownloadExcel(title, transformedData);
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "90%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          flex: 1,
          flexDirection: "column",
        }}
      >
        {transformedData.length === 0 ? (
          <Typography>No data available</Typography>
        ) : (
          <ResponsiveContainer width="100%" height="100%" sx={{ paddingLeft: "20px" }}>
            <BarChart data={transformedData} margin={{ top: 20, right: 15, bottom: 0, left: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#777" />
              <XAxis dataKey="month" stroke="#fff" />
              <YAxis
                stroke="#fff"
                tickFormatter={(value) => (formatAsCurrency ? `$${value.toLocaleString()}` : value)}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.05)" }} />

              {LegendOnLeft ? (
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  wrapperStyle={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: 12,
                    color: "#fff",
                    paddingLeft: 20,
                  }}
                />
              ) : (
                <Legend
                  wrapperStyle={{
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: 12,
                    padding: 10,
                    color: "#fff", // optional, in case you want white text
                  }}
                />
              )}
              {lobKeys.map((lob, index) => (
                <Bar key={lob} dataKey={lob} stackId="a" fill={getColor(lob, index)}>
                  {index === lobKeys.length - 1 && (
                    <LabelList
                      dataKey="_totalValue"
                      position="top"
                      fill="#fff"
                      fontSize={14}
                      fontWeight="bold"
                      formatter={(value) =>
                        formatAsCurrency
                          ? `$${value.toLocaleString(undefined, {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}`
                          : value
                      }
                    />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 1,
          mt: LegendOnLeft ? 1 : 2,
          flexWrap: "wrap",
        }}
      >
        {vendors.map((vendor) => (
          <IconButton
            key={vendor}
            size="small"
            onClick={() => setActiveVendor(vendor)}
            sx={{
              borderRadius: 2,
              fontSize: 10,
              fontWeight: "bold",
              backgroundColor: activeVendor === vendor ? "rgba(255, 255, 255, 0.1)" : "transparent",
              color: activeVendor === vendor ? "#fff" : "rgba(255, 255, 255, 0.7)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            {vendor.toUpperCase()}
          </IconButton>
        ))}
      </Box>
    </WidgetContainer>
  );
};

export default StackedBarChartGeneric;
